import React, { useState, useRef, useEffect } from 'react';
import sparkApi from '../../../api/SparkApi'; // adjust path
import jwt_decode from 'jwt-decode';
import EmojiPicker from 'emoji-picker-react';
import DataApi from '../../../api/DataApi';
import WatconnectApi from '../../../api/WatconnectApi';
import FileAttachmentModal from './FileAttachmentModal';
import TemplateModal from './TemplateModal';
import { hasPermission, isAdmin, getUserPermissions } from '../../../utils/permissionCache';

const WhatsAppChat = ({
  isOpen,
  onToggle,
  onClose,
  recipientName,
  recipientPhone,
  socket,
  selectedWhatsAppSetting,
}) => {
  const dataApi = new DataApi('watconnect_history');

  const [userInfo] = useState(
    jwt_decode(localStorage.getItem('watconnect_token'))
  );
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showMemberInfo, setShowMemberInfo] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [receivedMessage, setReceivedMessage] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showFileAttachment, setShowFileAttachment] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [canDeleteChat, setCanDeleteChat] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);

  useEffect(() => {
    const checkDeletePermission = async () => {
      try {
        if (userInfo && isAdmin(userInfo)) {
          setCanDeleteChat(true);
          return;
        }

        const token = localStorage.getItem('watconnect_token') || localStorage.getItem('token');
        if (token) {
          try {
            const decoded = jwt_decode(token);
            if (isAdmin(decoded)) {
              setCanDeleteChat(true);
              return;
            }
          } catch (e) { }
        }

        const canDelHistory = await hasPermission('watconnect_history', 'delete');
        const canDelWatconnect = await hasPermission('watconnect', 'delete');
        const canDelMsgHistory = await hasPermission('message_history', 'delete');

        if (canDelHistory || canDelWatconnect || canDelMsgHistory) {
          setCanDeleteChat(true);
          return;
        }

        const perms = await getUserPermissions();
        if (Array.isArray(perms)) {
          const match = perms.find(p => {
            const name = (p.object_name || '').toLowerCase();
            return (name.includes('watconnect') || name.includes('history') || name.includes('message')) && (p.can_delete || p.modify_all);
          });
          if (match) {
            setCanDeleteChat(true);
            return;
          }
        }

        setCanDeleteChat(false);
      } catch (err) {
        console.error('Error checking delete permission for chat:', err);
        setCanDeleteChat(false);
      }
    };

    checkDeletePermission();
  }, [userInfo]);

  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const shouldScrollToBottomRef = useRef(true);

  // File icons
  const pdfIcon = '/user_images/pdf-icon-png.jpg';
  const docxIcon = '/user_images/doc-icon.png';
  const xlsIcon = '/user_images/xls-icon.png';
  const mapIcon = '/user_images/google-maps.jpg';
  const textIcon = '/user_images/text-file.jpg';
  // ✅ 1️⃣ Socket listener — listen to received, outgoing, and status socket events
  useEffect(() => {
    if (!socket) return;

    const handleMessageEvent = data => {
      const msgData = Array.isArray(data) ? data[0] : data;
      if (typeof data === 'string') {
        if (data === selectedWhatsAppSetting) {
          shouldScrollToBottomRef.current = true;
          getMessageHistoryRecords(selectedWhatsAppSetting, 0, false);
        }
        return;
      }

      const incomingNum =
        msgData?.recipient_id || msgData?.to || msgData?.from || msgData?.whatsapp_number;
      if (incomingNum) {
        const socketNumber = String(incomingNum).replace(/\D/g, '');
        const currentNumber = recipientPhone.replace(/\D/g, '');
        if (socketNumber === currentNumber) {
          shouldScrollToBottomRef.current = true;
          getMessageHistoryRecords(selectedWhatsAppSetting, 0, false);
        }
      }
    };

    const handleStatusEvent = data => {
      const statusObj = Array.isArray(data) ? data[0] : data;
      if (statusObj?.business_number === selectedWhatsAppSetting) {
        shouldScrollToBottomRef.current = true;
        getMessageHistoryRecords(selectedWhatsAppSetting, 0, false);
      }
    };

    socket.on('receivedwhatsappmessage', handleMessageEvent);
    socket.on('outgoingwhatsappmessage', handleMessageEvent);
    socket.on('whatsapp_statuses', handleStatusEvent);

    return () => {
      socket.off('receivedwhatsappmessage', handleMessageEvent);
      socket.off('outgoingwhatsappmessage', handleMessageEvent);
      socket.off('whatsapp_statuses', handleStatusEvent);
    };
  }, [socket, recipientPhone, selectedWhatsAppSetting]);

  // ✅ 2️⃣ Fetch history — only when user opens chat or changes WhatsApp setting
  useEffect(() => {
    if (recipientPhone && selectedWhatsAppSetting) {
      setOffset(0);
      setHasMore(true);
      shouldScrollToBottomRef.current = true;
      getMessageHistoryRecords(selectedWhatsAppSetting, 0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientPhone, selectedWhatsAppSetting]);

  // ✅ 3️⃣ Function to fetch records with pagination support
  const LIMIT = 50;

  const getMessageHistoryRecords = async (
    selectedWhatsAppSetting,
    currentOffset = 0,
    prepend = false
  ) => {
    if (recipientPhone && selectedWhatsAppSetting) {
      setIsLoading(true);
      let result = await dataApi.fetchById(
        `records/${recipientPhone}?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}&limit=${LIMIT}&offset=${currentOffset}`
      );
      setIsLoading(false);
      if (result.data?.success) {
        const formatted = (result.data?.records || []).map(r => {
          let parsedParams = r.params;
          if (typeof parsedParams === 'string') {
            try {
              parsedParams = JSON.parse(parsedParams);
            } catch (e) {
              console.error("Failed to parse params in frontend:", e);
            }
          }
          return {
            ...r.history,
            template: r.template,
            params: parsedParams,
            createdbyname: r.createdbyname || r.history?.createdbyname || r.owner_name || r.history?.owner_name || null,
            owner_name: r.owner_name || r.history?.owner_name || r.createdbyname || r.history?.createdbyname || null,
          };
        });

        if (prepend) {
          const container = chatMessagesRef.current;
          const oldScrollHeight = container ? container.scrollHeight : 0;

          setMessages(prev => {
            const merged = [...formatted, ...prev];
            const unique = [];
            const seen = new Set();
            for (const msg of merged) {
              if (msg && msg.id) {
                if (!seen.has(msg.id)) {
                  seen.add(msg.id);
                  unique.push(msg);
                }
              } else {
                unique.push(msg);
              }
            }
            return unique;
          });

          if (formatted.length < LIMIT) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }

          setTimeout(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop = newScrollHeight - oldScrollHeight;
            }
          }, 50);
        } else {
          setMessages(formatted);
          if (formatted.length < LIMIT) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }

          const container = chatMessagesRef.current;
          setTimeout(() => {
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          }, 100);
        }
      } else {
        if (!prepend) {
          setMessages([]);
        }
        setHasMore(false);
      }
    } else {
      if (!prepend) {
        setMessages([]);
      }
      setHasMore(false);
    }
  };

  // ✅ 4️⃣ Auto-scroll — Stable fix with delay and cleanup
  useEffect(() => {
    if (!messages.length) return;
    if (!shouldScrollToBottomRef.current) {
      // Skip auto-scroll to bottom, we are loading older messages
      return;
    }

    const timeout = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }, 200); // Wait 200ms for DOM + media to render

    return () => clearTimeout(timeout);
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = e => {
      // if clicked outside emoji picker and smile icon
      if (
        !e.target.closest('.wa-emoji-picker') &&
        !e.target.closest('.fa-face-smile')
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const onEmojiClick = emojiData => {
    setNewMessage(prev => prev + emojiData.emoji);
    // setShowEmojiPicker(false);
  };

  const handleFileSendComplete = () => {
    // Refresh messages after file send
    setOffset(0);
    setHasMore(true);
    shouldScrollToBottomRef.current = true;
    getMessageHistoryRecords(selectedWhatsAppSetting, 0, false);
  };
  const extractParameters = text => {
    if (!text) return [];
    const paramRegex = /\{\{(\d+)\}\}/g;
    const params = [];
    let match;
    while ((match = paramRegex.exec(text)) !== null) {
      params.push(parseInt(match[1]));
    }
    return [...new Set(params)].sort((a, b) => a - b);
  };
  const handleTemplateSend = async submittedData => {
    const log = (msg, data = null) => console.log(`🔹 ${msg}`, data ?? '');
    const logError = (msg, err) =>
      console.error(`❌ ${msg}`, err?.response?.data || err);

    // Utility: fail-fast if condition is false
    const assertSuccess = (condition, message) => {
      if (!condition) throw new Error(message);
    };

    const {
      selectedTemplate,
      selectedWhatsAppSetting,
      parameterValues,
      selectedFile,
    } = submittedData;

    try {
      log('🚀 Sending Template Started');
      const watconnectApi = new WatconnectApi('webhook_template');
      const historyApi = new DataApi('watconnect');

      log('📦 Selected Template:', selectedTemplate);
      log('📱 Recipient:', recipientPhone, recipientName);
      log('⚙️ Selected WhatsApp Setting:', selectedWhatsAppSetting);

      // ========== STEP 1: Upload Media ==========
      const mediaUploads = {};
      const headerComponents =
        selectedTemplate.components?.filter(comp => comp.type === 'HEADER') ||
        [];

      for (const component of headerComponents) {
        if (
          component.format === 'IMAGE' ||
          component.format === 'VIDEO' ||
          component.format === 'DOCUMENT'
        ) {
          try {
            let mediaId;

            if (selectedFile) {
              log(
                `📤 Uploading custom ${component.format} file: ${selectedFile.name}`
              );
              const formData = new FormData();
              formData.append('messaging_product', 'whatsapp');
              formData.append('file', selectedFile);
              formData.append('description', component.format.toLowerCase());

              const uploadResponse = await watconnectApi.post(
                `/documentId?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting)}`,
                formData
              );
              mediaId = uploadResponse?.data?.id || uploadResponse?.id;
              log(`✅ Uploaded custom file. Media ID: ${mediaId}`);
            } else {
              log(`📄 Getting ${component.format} Header from existing file`);

              const fileUrl = selectedTemplate.file_data?.file_url;
              assertSuccess(
                fileUrl,
                `No file URL for ${component.format} header`
              );

              log(`📁 Using existing file: ${fileUrl}`);

              try {
                log(
                  `📤 Requesting backend to download and upload file from URL to WhatsApp: ${fileUrl}`
                );
                const uploadResponse = await watconnectApi.post(
                  `/upload_whatsapp_media?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting)}`,
                  {
                    header: component.format,
                    header_body: fileUrl,
                  }
                );

                log('📤 Backend upload response:', uploadResponse);

                mediaId =
                  uploadResponse?.documentId ||
                  uploadResponse?.data?.documentId;
              } catch (fetchErr) {
                logError(
                  'Failed to upload existing file via server-side upload_whatsapp_media',
                  fetchErr
                );
                throw fetchErr;
              }
            }

            assertSuccess(
              mediaId,
              `Failed to get media ID for ${component.format} header`
            );

            mediaUploads[`header_${component.format}`] = mediaId;
            log(`✅ Got media ID for ${component.format} header: ${mediaId}`);
          } catch (err) {
            logError(`${component.format} Header Media ID Failed`, err);
            alert(`❌ Media upload failed `);
            setShowTemplateModal(false);
            return; // ⛔ Stop further execution
          }
        }
      }

      // ========== STEP 2: Build WhatsApp Payload ==========
      log('🧩 Building Template Payload Components');

      const templatePayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'template',
        template: {
          name: selectedTemplate.name,
          language: {
            code: selectedTemplate.language || 'en',
            policy: 'deterministic',
          },
          components: [],
        },
      };

      const components = [];

      // HEADER
      selectedTemplate.components?.forEach(component => {
        if (component.type === 'HEADER') {
          const headerComponent = { type: 'header', parameters: [] };
          if (
            component.format === 'IMAGE' ||
            component.format === 'VIDEO' ||
            component.format === 'DOCUMENT'
          ) {
            const mediaId = mediaUploads[`header_${component.format}`];
            if (mediaId) {
              if (
                component.format === 'IMAGE' ||
                component.format === 'VIDEO'
              ) {
                headerComponent.parameters.push({
                  type: component.format.toLowerCase(),
                  [component.format.toLowerCase()]: { id: mediaId },
                });
              } else if (component.format === 'DOCUMENT') {
                headerComponent.parameters.push({
                  type: 'document',
                  document: {
                    id: mediaId,
                    filename:
                      selectedTemplate.file_data?.file_name || 'document.pdf',
                  },
                });
              }
            }
          }
          if (headerComponent.parameters.length > 0)
            components.push(headerComponent);
        }
      });

      const resolveParamValue = (val, clientName, isParam1 = false) => {
        if (!val) return '';
        if (!isParam1) return val;
        const trimmed = String(val).trim();
        const lower = trimmed.toLowerCase();
        if (
          lower === 'name' ||
          lower === '{{name}}' ||
          lower === '{name}' ||
          lower === 'client_name' ||
          lower === '{{client_name}}' ||
          lower === 'client name' ||
          lower === '{{client name}}'
        ) {
          return clientName || val;
        }
        return val;
      };

      // BODY
      selectedTemplate.components?.forEach(component => {
        if (component.type === 'BODY') {
          const bodyComponent = { type: 'body', parameters: [] };
          const params = extractParameters(component.text);
          params.forEach(param => {
            let val =
              parameterValues[`body_${param}_fallback`] ||
              parameterValues[`body_${param}`] ||
              parameterValues[`body_${param}_field`] ||
              parameterValues[`${param}_fallback`] ||
              parameterValues[param] ||
              '';

            if (typeof val === 'string' && val.trim().startsWith('{{')) {
              const fallback =
                parameterValues[`body_${param}_fallback`] ||
                parameterValues[`${param}_fallback`];
              if (fallback && String(fallback).trim()) {
                val = String(fallback).trim();
              }
            }

            val = resolveParamValue(val, recipientName, param === 1);

            if (!val || (typeof val === 'string' && val.trim().startsWith('{{'))) {
              val = (param === 1 ? recipientName : null) || `Value ${param}`;
            }

            bodyComponent.parameters.push({ type: 'text', text: String(val).trim() });
          });
          if (bodyComponent.parameters.length > 0)
            components.push(bodyComponent);
        }
      });

      templatePayload.template.components = components;

      log(
        '📨 Final Template Payload:',
        JSON.stringify(templatePayload, null, 2)
      );

      // ========== STEP 3: Send WhatsApp Message ==========
      let messageResponse;
      try {
        log('📤 Sending WhatsApp Template Message...');
        messageResponse = await watconnectApi.post(
          `/message?whatsapp_setting_number=${encodeURIComponent(
            selectedWhatsAppSetting
          )}`,
          templatePayload
        );
        log('📬 Message Response:', messageResponse);
        assertSuccess(
          messageResponse?.messages?.[0]?.id,
          'Failed to send WhatsApp message'
        );
      } catch (err) {
        logError('Message Send Failed', err);
        alert(
          `❌ Failed to send WhatsApp message: ${err.response?.data?.message || err.message
          }`
        );
        setShowTemplateModal(false);
        return; // ⛔ Stop further execution
      }

      // ========== STEP 4: Create History Record ==========
      let historyRecord;
      try {
        const historyPayload = {
          name: recipientName || '',
          template_name: selectedTemplate.name || '',
          whatsapp_number: recipientPhone.replace(/^\+/, ''),
          message: ``,
          status: 'Outgoing',
          business_number: selectedWhatsAppSetting,
          delivery_status: 'sent',
          message_id: messageResponse?.messages?.[0]?.id,
        };
        log('🗂 Creating History Record:', historyPayload);
        const historyRes = await historyApi.post(`/history`, historyPayload);
        log('🗄 History Response:', historyRes);
        historyRecord = historyRes?.data?.data || historyRes;
        assertSuccess(historyRecord?.id, 'Failed to create message history');
      } catch (err) {
        logError('History Creation Failed', err);
        alert('❌ Failed to create message history.');
        setShowTemplateModal(false);
        return; // ⛔ Stop further execution
      }

      // ========== STEP 5: Update Parameters in History ==========
      try {
        const parametersData = {};
        const mainParams = {};

        Object.keys(parameterValues).forEach(key => {
          if (key.startsWith('body_')) {
            const parts = key.split('_');
            const paramNumber = parts[1];
            let value = parameterValues[key];
            if (value) {
              value = resolveParamValue(value, recipientName);
              mainParams[key] = value;
              if (!mainParams[paramNumber] || mainParams[paramNumber].startsWith('{{')) {
                mainParams[paramNumber] = value;
              }
            }
          } else if (
            ['file_id', 'file_url', 'file_name', 'file_type', 'mime_type'].includes(key)
          ) {
            if (parameterValues[key]) mainParams[key] = parameterValues[key];
          }
        });

        const allBodyParams = extractParameters(
          selectedTemplate.components?.find(c => c.type === 'BODY')?.text || ''
        );
        allBodyParams.forEach(p => {
          const posKey = String(p);
          if (!mainParams[posKey] || mainParams[posKey].startsWith('{{')) {
            let val =
              parameterValues[`body_${p}`] ||
              parameterValues[`body_${p}_fallback`] ||
              parameterValues[`body_${p}_field`] ||
              parameterValues[p] ||
              parameterValues[`${p}_fallback`] ||
              (p === 1 ? recipientName : null) ||
              `Value ${p}`;
            val = resolveParamValue(val, recipientName);
            mainParams[posKey] = String(val).trim();
          }
          const fbKey = `${posKey}_fallback`;
          if (!mainParams[fbKey] && parameterValues[`body_${p}_fallback`]) {
            mainParams[fbKey] = parameterValues[`body_${p}_fallback`];
          }
        });

        if (selectedFile) {
          try {
            const attachmentsApi = new DataApi('attachments');
            const fileForm = new FormData();
            if (
              selectedFile.length !== undefined &&
              typeof selectedFile !== 'string'
            ) {
              for (let i = 0; i < selectedFile.length; i++) {
                fileForm.append('files', selectedFile[i]);
              }
            } else {
              fileForm.append('files', selectedFile);
            }
            fileForm.append('description', 'template custom attachment');

            const attachmentRes = await attachmentsApi.post(
              `/${historyRecord.id}`,
              fileForm
            );

            const resData = attachmentRes?.data;
            const attachments =
              resData?.records || resData?.data || (Array.isArray(resData) ? resData : []);
            const uploadedAtt = attachments[0];

            if (uploadedAtt) {
              mainParams.file_id = uploadedAtt.id || mainParams.file_id;
              mainParams.file_url = uploadedAtt.file_url || mainParams.file_url;
              mainParams.file_type = uploadedAtt.file_type || uploadedAtt.filetype || mainParams.file_type;
              mainParams.file_name = uploadedAtt.file_name || selectedFile.name;
              mainParams.mime_type = uploadedAtt.mime_type || selectedFile.type;
            } else if (selectedFile) {
              mainParams.file_name = selectedFile.name;
              mainParams.mime_type = selectedFile.type;
            }
          } catch (attErr) {
            console.error('Error uploading attachment for history record:', attErr);
          }
        } else if (selectedTemplate.file_data) {
          mainParams.file_url = selectedTemplate.file_data.file_url;
          mainParams.file_type = selectedTemplate.file_data.file_type;
          mainParams.file_name = selectedTemplate.file_data.file_name;
          mainParams.mime_type = selectedTemplate.file_data.mime_type;
        }

        if (Object.keys(mainParams).length > 0) {
          parametersData.main = mainParams;
          if (mainParams.file_id) parametersData.file_id = mainParams.file_id;
          if (mainParams.file_url) parametersData.file_url = mainParams.file_url;
          if (mainParams.file_name) parametersData.file_name = mainParams.file_name;
          if (mainParams.file_type) parametersData.file_type = mainParams.file_type;
          if (mainParams.mime_type) parametersData.mime_type = mainParams.mime_type;
        }

        log('🧩 Updating History Parameters:', parametersData);

        await historyApi.post(`/history`, {
          id: historyRecord.id,
          parameters: parametersData,
        });
      } catch (err) {
        logError('History Parameter Update Failed', err);
        // Note: not a fatal step, history already exists
      }

      log('🎉 Template Sending Completed Successfully!');
    } catch (err) {
      logError('Unhandled Template Send Error', err);
      alert(
        'Failed to send template: ' +
        (err.response?.data?.message || err.message)
      );
    }

    setShowTemplateModal(false);
    setOffset(0);
    setHasMore(true);
    shouldScrollToBottomRef.current = true;
    getMessageHistoryRecords(selectedWhatsAppSetting, 0, false);
  };

  const handleSendMessage = async () => {
    const textToSend = newMessage.trim();
    if (!textToSend || isSendingRef.current || isSending) return;

    isSendingRef.current = true;
    setIsSending(true);
    setNewMessage('');

    try {
      // 1️⃣ Prepare WhatsApp payload
      const whatsappPayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'text',
        text: {
          preview_url: false,
          body: textToSend,
        },
      };

      // 2️⃣ Send message via WhatsApp API
      const watconnectApi = new WatconnectApi('webhook_template');
      const whatsappResponse = await watconnectApi.post(
        `/message?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`,
        whatsappPayload
      );

      // 3️⃣ Prepare DB update payload for WatConnect history
      const updateData = {
        name: recipientName || '',
        whatsapp_number: recipientPhone.replace(/^\+/, ''),
        message: textToSend,
        status: 'Outgoing',
        business_number: selectedWhatsAppSetting,
        delivery_status: 'sent',
        message_id: whatsappResponse?.messages?.[0]?.id || null,
      };

      // 4️⃣ Save to DB
      const historyApi = new DataApi('watconnect');
      await historyApi.post(`/history`, updateData);

      // 5️⃣ Update UI instantly (no need to wait for API refetch)
      const msgObj = {
        id: Date.now().toString(),
        message: textToSend,
        status: 'Outgoing',
        delivery_status: 'sent',
        is_read: false,
        created_at: new Date().toISOString(),
        template: null,
        params: null,
      };

      shouldScrollToBottomRef.current = true;
      setMessages(prev => [...prev, msgObj]);
    } catch (error) {
      console.error('❌ Error sending WhatsApp message:', error);
      setNewMessage(textToSend);
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = dateString => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true, // 👈 enables 12-hour AM/PM format
    });
  };

  const getInitials = name => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  const getDateLabel = dateString => {
    const date = new Date(dateString);
    const today = new Date();

    const isToday = date.toDateString() === today.toDateString();

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return date.toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
  const renderMessageContent = msg => {
    const elements = [];

    // Check if this is a template message
    const isTemplate = msg.template && msg.template.template_name;

    if (isTemplate) {
      // ===== TEMPLATE MESSAGE RENDERING =====
      const template = msg.template;
      const params = msg.params || {};

      // 1️⃣ HEADER with Media (Top of template)
      if (
        template.header === 'IMAGE' ||
        template.header === 'VIDEO' ||
        template.header === 'DOCUMENT' ||
        template.header === 'TEXT'
      ) {
        const mainFileUrl = params.main?.file_url || params.file_url || msg.template?.file_data?.file_url || msg.file_url;
        const mainFileName = params.main?.file_name || params.file_name || msg.template?.file_data?.file_name || 'Document';
        const mainFileType =
          params.main?.file_type?.toLowerCase().replace('.', '') ||
          params.file_type?.toLowerCase().replace('.', '') ||
          msg.template?.file_data?.file_type?.toLowerCase().replace('.', '') ||
          '';
        const mimeType = params.main?.mime_type || params.mime_type || msg.template?.file_data?.mime_type || '';

        const isPdfOrDoc =
          mainFileType === 'pdf' ||
          mainFileType === 'csv' ||
          mainFileName.toLowerCase().endsWith('.pdf') ||
          mainFileName.toLowerCase().endsWith('.csv') ||
          mainFileName.toLowerCase().endsWith('.doc') ||
          mainFileName.toLowerCase().endsWith('.docx') ||
          mainFileName.toLowerCase().endsWith('.xls') ||
          mainFileName.toLowerCase().endsWith('.xlsx') ||
          (mainFileUrl && mainFileUrl.toLowerCase().split('?')[0].endsWith('.pdf')) ||
          (mainFileUrl && mainFileUrl.toLowerCase().split('?')[0].endsWith('.csv')) ||
          mimeType === 'application/pdf' ||
          mimeType === 'text/csv';

        if (template.header === 'TEXT') {
          // TEXT header - use header_body
          if (template.header_body && template.header_body !== 'null') {
            elements.push(
              <div key='template-header-text' className='template-header-text'>
                {template.header_body}
              </div>
            );
          }
        } else if (mainFileUrl) {
          if (isPdfOrDoc || template.header === 'DOCUMENT') {
            elements.push(
              <div key='template-header-doc' className='wa-attachment'>
                <a
                  href={mainFileUrl}
                  target='_blank'
                  rel='noreferrer'
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className='document-preview' style={{ cursor: 'pointer' }}>
                    <div className='document-icon'>📄</div>
                    <div className='document-info'>
                      <div className='document-name' title={mainFileName}>
                        {mainFileName}
                      </div>
                      <div className='document-size'>
                        {params.main?.file_size
                          ? `(${(params.main.file_size / 1024).toFixed(1)} KB)`
                          : 'Click to view document'}
                      </div>
                    </div>
                    <i className='fa-solid fa-arrow-down-to-line text-secondary ms-2'></i>
                  </div>
                </a>
              </div>
            );
          } else if (template.header === 'IMAGE') {
            elements.push(
              <div key='template-header-img' className='wa-attachment'>
                <a href={mainFileUrl} target='_blank' rel='noreferrer'>
                  <img
                    src={mainFileUrl}
                    alt='Header'
                    className='wa-chat-image'
                    style={{
                      maxHeight: '200px',
                      objectFit: 'cover',
                      width: '100%',
                      borderRadius: '6px',
                    }}
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                </a>
              </div>
            );
          } else if (template.header === 'VIDEO') {
            elements.push(
              <div key='template-header-vid' className='wa-attachment'>
                <video controls className='wa-chat-video' style={{ maxHeight: '200px', width: '100%' }}>
                  <source src={mainFileUrl} type={mimeType || `video/${mainFileType}`} />
                </video>
              </div>
            );
          }
        } else {
          // Fallback if no file URL
          elements.push(
            <div key='template-header-placeholder' className='wa-attachment'>
              <div className='media-preview-placeholder'>
                {template.header === 'IMAGE'
                  ? '🖼️ Image Header'
                  : template.header === 'VIDEO'
                    ? '🎥 Video Header'
                    : '📄 Document Header'}
              </div>
            </div>
          );
        }
      }

      // 2️⃣ BODY (Message content with parameters replaced)
      if (template.message_body) {
        let bodyText = template.message_body;

        const isUuid = str =>
          typeof str === 'string' &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());

        const isNameKey = str => {
          if (!str) return false;
          const s = String(str).trim().toLowerCase().replace(/[\{\}\s_]/g, '');
          return s === 'name' || s === 'leadname' || s === 'recipientname' || s === 'customername';
        };

        const recipientContactName =
          recipientName ||
          msg.recipient_name ||
          msg.recipientname ||
          msg.customer_name ||
          (msg.name !== msg.createdbyname && msg.name !== msg.sender_name ? msg.name : '') ||
          '';

        const mainParams = params.main || params || {};
        const placeholdersInBody = [];
        const regex = /\{\{([^}]+)\}\}/g;
        let match;
        while ((match = regex.exec(template.message_body)) !== null) {
          placeholdersInBody.push(match[1].trim());
        }

        const uniquePlaceholders = [...new Set(placeholdersInBody)];

        uniquePlaceholders.forEach((ph, index) => {
          const posIdxStr = String(index + 1);
          let val =
            mainParams[ph] !== undefined
              ? mainParams[ph]
              : (mainParams[posIdxStr] !== undefined
                  ? mainParams[posIdxStr]
                  : (mainParams[`body_${ph}`] !== undefined
                      ? mainParams[`body_${ph}`]
                      : (mainParams[`body_${posIdxStr}`] !== undefined
                          ? mainParams[`body_${posIdxStr}`]
                          : (mainParams[ph.toLowerCase()] !== undefined
                              ? mainParams[ph.toLowerCase()]
                              : null))));

          if (typeof val === 'object' && val !== null) {
            val = val.value || val.displayValue || val.field || '';
          }

          if (val !== null && val !== undefined) {
            val = String(val).trim();
          }

          if (val === null || val === undefined || val === '' || val.startsWith('{{') || val === `{{${ph}}}`) {
            const fallbackVal =
              mainParams[`${ph}_fallback`] ||
              mainParams[`${posIdxStr}_fallback`] ||
              mainParams[`body_${ph}_fallback`] ||
              mainParams[`body_${posIdxStr}_fallback`] ||
              mainParams[`fallback_${ph}`] ||
              mainParams[`fallback_${posIdxStr}`];
            if (fallbackVal !== undefined && fallbackVal !== null && String(fallbackVal).trim() !== '') {
              val = String(fallbackVal).trim();
            }
          }

          if ((!val || val.startsWith('{{')) && (ph === '1' || isNameKey(ph))) {
            if (recipientContactName && recipientContactName !== 'System') {
              val = recipientContactName;
            }
          }

          if (val !== null && val !== undefined && val !== '') {
            bodyText = bodyText.replace(
              new RegExp(`\\{\\{${ph}\\}\\}`, 'g'),
              val
            );
          }
        });

        Object.keys(mainParams).forEach(key => {
          if (
            key !== 'file_url' &&
            key !== 'file_type' &&
            key !== 'file_name' &&
            key !== 'mime_type' &&
            key !== 'file_size' &&
            key !== 'main'
          ) {
            let val = mainParams[key];
            if (typeof val === 'object' && val !== null) {
              val = val.value || val.displayValue || '';
            }
            if (val !== null && val !== undefined) {
              val = String(val).trim();
              if (isNameKey(key) || isNameKey(val) || isUuid(val)) {
                if (recipientContactName && recipientContactName !== 'System') {
                  val = recipientContactName;
                }
              }
              bodyText = bodyText.replace(
                new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
                val
              );
            }
          }
        });

        elements.push(
          <div key='template-body' className='wa-template-body'>
            {bodyText.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      }

      // 3️⃣ FOOTER
      if (template.footer && template.footer.trim().toLowerCase() !== 'null') {
        elements.push(
          <div key='template-footer' className='wa-template-footer'>
            {template.footer}
          </div>
        );
      }

      // 4️⃣ CAROUSEL CARDS
      if (template.cards && template.cards.length > 0) {
        const carouselCards = template.cards.map((card, cardIndex) => {
          const cardParams = params.carousel?.[cardIndex] || {};

          return (
            <div key={`carousel-card-${cardIndex}`} className='carousel-card'>
              {/* Card Header Media */}
              {card.header?.format === 'IMAGE' && cardParams.file_url && (
                <div className='carousel-header'>
                  <img
                    src={cardParams.file_url}
                    alt={`Card ${cardIndex + 1}`}
                    className='wa-carousel-media'
                  />
                </div>
              )}
              {card.header?.format === 'VIDEO' && cardParams.file_url && (
                <div className='carousel-header'>
                  <video controls className='wa-carousel-media'>
                    <source src={cardParams.file_url} />
                  </video>
                </div>
              )}

              {/* Card Body */}
              {card.body?.text && (
                <div className='carousel-body'>
                  {card.body.text.split('\n').map((line, i) => {
                    let processedLine = line;
                    // Replace parameters like {{1}}
                    Object.keys(cardParams).forEach(key => {
                      if (
                        key !== 'file_url' &&
                        key !== 'file_type' &&
                        key !== 'file_name'
                      ) {
                        processedLine = processedLine.replace(
                          `{{${key}}}`,
                          cardParams[key]
                        );
                      }
                    });
                    return <div key={i}>{processedLine}</div>;
                  })}
                </div>
              )}

              {/* Card Buttons */}
              {card.buttons && card.buttons.length > 0 && (
                <div className='carousel-buttons'>
                  {card.buttons.map((btn, btnIdx) => (
                    <div key={btnIdx} className='carousel-button'>
                      {btn.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        });

        elements.push(
          <div key='carousel-wrapper' className='template-carousel'>
            <div className='carousel-container'>{carouselCards}</div>
          </div>
        );
      }
    } else {
      // ===== NON-TEMPLATE MESSAGE RENDERING =====

      // --- Media Attachments ---
      const fileUrl = msg.params?.main?.file_url || msg.params?.file_url || msg.file_url;
      const fileName = msg.params?.main?.file_name || msg.params?.file_name || msg.file_name || 'Document';
      if (fileUrl) {
        let file_type =
          msg.params?.main?.file_type?.toLowerCase().replace('.', '') ||
          msg.params?.file_type?.toLowerCase().replace('.', '') ||
          msg.file_type?.toLowerCase().replace('.', '') ||
          '';
        const file_url = fileUrl;
        file_type = (file_type || '').toLowerCase().replace('.', '');

        const extension = file_url.split('.').pop()?.toLowerCase().split('?')[0] || '';
        if (!file_type || file_type === 'undefined' || file_type === 'null') {
          file_type = extension;
        }

        // Normalize to MIME type
        if (file_type === 'mp4') file_type = 'video/mp4';
        else if (['jpeg', 'jpg'].includes(file_type)) file_type = 'image/jpeg';
        else if (file_type === 'png') file_type = 'image/png';
        else if (file_type === 'gif') file_type = 'image/gif';
        else if (file_type === 'pdf') file_type = 'application/pdf';
        else if (file_type === 'doc' || file_type === 'docx')
          file_type = 'application/msword';
        else if (file_type === 'xls' || file_type === 'xlsx')
          file_type = 'application/vnd.ms-excel';
        else if (file_type === 'mp3') file_type = 'audio/mpeg';
        else if (file_type === 'ogg') file_type = 'audio/ogg';
        else if (file_type === 'aac') file_type = 'audio/aac';
        else if (
          !file_type.startsWith('image/') &&
          !file_type.startsWith('video/') &&
          !file_type.startsWith('audio/') &&
          !file_type.startsWith('application/')
        ) {
          if (['jpg', 'jpeg', 'png', 'gif'].includes(extension))
            file_type = 'image/' + extension;
          else if (['mp4', 'mov', 'avi'].includes(extension))
            file_type = 'video/' + extension;
          else if (['mp3', 'ogg', 'aac'].includes(extension))
            file_type = 'audio/' + extension;
          else file_type = 'application/' + extension;
        }

        // IMAGE
        if (file_type.startsWith('image/')) {
          elements.push(
            <div key='img' className='wa-attachment'>
              <a href={file_url} target='_blank' rel='noreferrer'>
                <img
                  src={file_url}
                  alt={fileName || 'attachment'}
                  className='wa-chat-image'
                  style={{
                    maxHeight: '200px',
                    objectFit: 'cover',
                    width: '100%',
                    borderRadius: '6px',
                  }}
                />
              </a>
            </div>
          );
        }
        // VIDEO
        else if (file_type.startsWith('video/')) {
          elements.push(
            <div key='vid' className='wa-attachment'>
              <video controls className='wa-chat-video' style={{ maxHeight: '200px', width: '100%' }}>
                <source src={file_url} type={file_type} />
              </video>
            </div>
          );
        }
        // AUDIO
        else if (file_type.startsWith('audio/')) {
          elements.push(
            <div key='aud' className='wa-attachment'>
              <audio controls className='wa-chat-audio'>
                <source src={file_url} type={file_type} />
              </audio>
            </div>
          );
        }
        // DOCS
        else {
          elements.push(
            <div key='doc' className='wa-attachment'>
              <a
                href={file_url}
                target='_blank'
                rel='noreferrer'
                download
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className='document-preview' style={{ cursor: 'pointer' }}>
                  <div className='document-icon'>📄</div>
                  <div className='document-info'>
                    <div className='document-name' title={fileName}>
                      {fileName}
                    </div>
                    <div className='document-size'>
                      {msg.params?.main?.file_size
                        ? `(${(msg.params.main.file_size / 1024).toFixed(1)} KB)`
                        : 'Click to view document'}
                    </div>
                  </div>
                  <i className='fa-solid fa-arrow-down-to-line text-secondary ms-2'></i>
                </div>
              </a>
            </div>
          );
        }
      }

      // --- Text Message ---
      if (msg.message) {
        elements.push(
          <div key='txt' className='wa-text ps-2'>
            {msg.message.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      }
    }

    // --- Buttons (for both template and non-template) ---
    const buttonsData =
      msg?.interactive_details || msg?.buttons || msg?.template?.temp_buttons;
    if (buttonsData && Array.isArray(buttonsData) && buttonsData.length > 0) {
      const buttonList = [];

      buttonsData.forEach((btn, idx) => {
        // If button contains list rows (Interactive List Message)
        if (Array.isArray(btn?.rows) && btn.rows.length > 0) {
          btn.rows.forEach((rowItem, rowIdx) => {
            const label = rowItem.title || rowItem.text || rowItem.id || '';
            if (label) {
              buttonList.push(
                <div key={`row-${idx}-${rowIdx}`} className='wa-interactive-button'>
                  {label}
                </div>
              );
            }
          });
        }
        // If button contains action.sections with rows
        else if (Array.isArray(btn?.action?.sections)) {
          btn.action.sections.forEach((sec, secIdx) => {
            if (Array.isArray(sec?.rows)) {
              sec.rows.forEach((rowItem, rIdx) => {
                const label = rowItem.title || rowItem.text || rowItem.id || '';
                if (label) {
                  buttonList.push(
                    <div key={`sec-${idx}-${secIdx}-${rIdx}`} className='wa-interactive-button'>
                      {label}
                    </div>
                  );
                }
              });
            }
          });
        }
        // Normal button or quick reply
        else {
          const label =
            btn.title ||
            btn.text ||
            btn.reply?.title ||
            btn.button_reply?.title ||
            '';
          if (label) {
            buttonList.push(
              <div key={`btn-${idx}`} className='wa-interactive-button'>
                {label}
              </div>
            );
          }
        }
      });

      if (buttonList.length > 0) {
        elements.push(
          <div key='btns' className='wa-interactive-buttons'>
            {buttonList}
          </div>
        );
      }
    }

    return <>{elements}</>;
  };

  const handleScroll = e => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && hasMore && !isLoading) {
      shouldScrollToBottomRef.current = false;
      const nextOffset = offset + LIMIT;
      setOffset(nextOffset);
      getMessageHistoryRecords(selectedWhatsAppSetting, nextOffset, true);
    }
  };

  const handleClearChat = async () => {
    if (!canDeleteChat) {
      alert("You do not have permission to clear chat history.");
      return;
    }

    if (!window.confirm("Are you sure you want to clear this chat history? This action cannot be undone.")) {
      return;
    }

    try {
      let response = await dataApi.delete(
        `history/${recipientPhone}?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`
      );
      if (response.status === 200 || response.data?.success) {
        setMessages([]);
        alert("Chat history cleared successfully!");
      } else {
        alert("Failed to clear chat history: " + (response.data?.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error clearing chat history:", error);
      alert("Error clearing chat history. Please try again.");
    }
  };

  return (
    <div className='wa-chat-container'>
      {/* Header */}
      <div className='wa-chat-header'>
        <div className='wa-chat-header-left'>
          <button className='wa-back-btn' onClick={onClose}>
            <i className='fa-solid fa-arrow-left'></i>
          </button>
          <div className='wa-chat-avatar'>
            <div className='wa-avatar-initials'>
              {getInitials(recipientName)}
            </div>
          </div>
          <div className='wa-chat-info'>
            <div className='wa-chat-name'>{recipientName || 'Unknown'}</div>
            <div className='wa-chat-status'>{recipientPhone || ''}</div>
          </div>
        </div>
        <div className='wa-chat-header-right'>
          {/* <button className='wa-header-btn'>
            <i className='fa-solid fa-phone'></i>
          </button> */}
          <button
            className='wa-header-btn'
            onClick={() => setShowTemplateModal(true)}
            title='Send Template'
          >
            <i className='fa-solid fa-layer-group'></i>
          </button>

          {canDeleteChat && (
            <button
              className='wa-header-btn'
              onClick={handleClearChat}
              title='Clear Chat'
            >
              <i className='fa-solid fa-trash-can'></i>
            </button>
          )}

          <button
            className='wa-header-btn'
            onClick={() => setShowMemberInfo(!showMemberInfo)}
            title='Contact Info'
          >
            <i className='fa-solid fa-ellipsis-vertical'></i>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        className='wa-chat-messages'
        ref={chatMessagesRef}
        onScroll={handleScroll}
      >
        <div className='wa-messages-container'>
          {isLoading && offset > 0 && (
            <div
              className='wa-chat-loading-more text-center py-2'
              style={{ width: '100%' }}
            >
              <small className='text-muted'>Loading older messages...</small>
            </div>
          )}
          {messages.map((msg, index) => {
            const currentDate = getDateLabel(msg.created_at);
            const prevMsg = messages[index - 1];
            const prevDate = prevMsg ? getDateLabel(prevMsg.created_at) : null;
            const showDate = currentDate !== prevDate;

            return (
              <React.Fragment key={`${msg.id}-${index}`}>
                {showDate && (
                  <div className='wa-date-separator'>
                    <span>{currentDate}</span>
                  </div>
                )}
                <div
                  className={`wa-message ${msg.status === 'Outgoing' ? 'wa-outgoing' : 'wa-incoming'
                    }`}
                >
                  <div className='wa-message-bubble'>
                    {(msg.createdbyname || msg.owner_name || msg.created_by_name || msg.sender_name) && (
                      <div
                        className='wa-message-sender-name'
                        style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: msg.status === 'Outgoing' ? '#075e54' : '#128c7e',
                          marginBottom: '3px',
                          textAlign: msg.status === 'Outgoing' ? 'right' : 'left',
                        }}
                      >
                        {msg.createdbyname || msg.owner_name || msg.created_by_name || msg.sender_name}
                      </div>
                    )}
                    <div className='wa-message-text'>
                      {renderMessageContent(msg)}
                    </div>
                    {msg.delivery_status === 'failed' && msg.err_messaage && (
                      <div className='message-error'>
                        <small className='text-danger'>
                          {msg.err_messaage}
                        </small>
                      </div>
                    )}

                    <div className='wa-message-time'>
                      <span>{formatTime(msg.created_at)}</span>
                      {msg.status === 'Outgoing' && (
                        <>
                          {msg.delivery_status === 'sent' && (
                            <i className='fa-solid fa-check text-secondary'></i>
                          )}
                          {msg.delivery_status === 'delivered' && (
                            <i className='fa-solid fa-check-double text-secondary'></i>
                          )}
                          {msg.delivery_status === 'read' && (
                            <i className='fa-solid fa-check-double wa-read'></i>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {isTyping && (
            <div className='wa-message wa-incoming'>
              <div className='wa-message-bubble'>
                <div className='wa-typing-indicator'>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className='wa-chat-input'>
        <div className='wa-input-container'>
          {/* Left Icons */}
          <div className='wa-left-icons'>
            {/* Emoji */}
            <button
              className='wa-input-btn'
              onClick={() => {
                setShowEmojiPicker(!showEmojiPicker);
              }}
            >
              <i className='fa-regular fa-face-smile'></i>
            </button>
          </div>

          {/* Textarea */}
          <textarea
            className='wa-message-input'
            placeholder='Type a message'
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            rows='1'
            maxLength={4096}
          />

          {/* Right Button */}
          {/* Right Button Section */}
          <div className='wa-right-icon'>
            {/* Attach Icon beside Mic */}
            <div className='wa-attach-wrapper'>
              <button
                className='wa-input-btn'
                onClick={() => {
                  setShowFileAttachment(!showFileAttachment);
                  setShowEmojiPicker(false);
                }}
              >
                <i className='fa-solid fa-paperclip'></i>
              </button>
            </div>

            {/* Mic or Send Button */}
            {newMessage.trim() ? (
              <button className='wa-send-btn' onClick={handleSendMessage}>
                <i className='fa-solid fa-paper-plane'></i>
              </button>
            ) : (
              <button className='wa-send-btn' disabled>
                <i className='fa-solid fa-paper-plane'></i>
              </button>
            )}
          </div>
        </div>
        {showEmojiPicker && (
          <div className='wa-emoji-picker'>
            <EmojiPicker onEmojiClick={onEmojiClick} />
          </div>
        )}{' '}
      </div>

      {showMemberInfo && (
        <div className='wa-member-info'>
          <div className='wa-member-header'>
            <button
              className='wa-back-btn'
              onClick={() => setShowMemberInfo(false)}
            >
              <i className='fa-solid fa-arrow-left'></i>
            </button>
            <span>Contact Info</span>
          </div>

          <div className='wa-member-content'>
            <div className='wa-member-avatar-section'>
              <div className='wa-member-avatar'>
                <div className='wa-avatar-initials large'>
                  {getInitials(recipientName)}
                </div>
              </div>
              <div className='wa-member-name'>{recipientName || 'Unknown'}</div>
              <div className='wa-member-phone'>+{recipientPhone}</div>
            </div>
          </div>
        </div>
      )}
      {showFileAttachment && (
        <FileAttachmentModal
          isOpen={showFileAttachment}
          onClose={() => setShowFileAttachment(false)}
          recipientPhone={recipientPhone}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          recipientName={recipientName}
          onSendComplete={handleFileSendComplete}
        />
      )}
      {showTemplateModal && (
        <TemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          recipientPhone={recipientPhone}
          recipientName={recipientName}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          onSendComplete={handleTemplateSend}
        />
      )}
    </div>
  );
};

export default WhatsAppChat;
